"use client"

import { useEffect, useRef } from "react"
import { initPostHog } from "@/lib/posthog"

/**
 * Read the ahead-experiment-id cookie (set by proxy) for consistent
 * PostHog identity across sessions. Returns undefined if not set.
 */
function getExperimentId(): string | undefined {
  if (typeof document === "undefined") return undefined
  const match = document.cookie.match(/(?:^|;\s*)ahead-experiment-id=([^;]+)/)
  return match?.[1]
}

/**
 * Read the edge-written `ahead-experiment-assignments`
 * cookie (`{ [flag]: variant }`, set by proxy.ts) and hand it to PostHog as
 * bootstrap flags. Because the edge bucketed these from the same
 * `ahead-experiment-id`, the SDK starts with the correct variant on init — no
 * flip when it finishes loading, and client-read surfaces (navbar, pricing)
 * that resolve from the same cookie stay in agreement.
 */
function getBootstrapFlags(): Record<string, string> | undefined {
  if (typeof document === "undefined") return undefined
  const match = document.cookie.match(
    /(?:^|;\s*)ahead-experiment-assignments=([^;]+)/
  )
  if (!match) return undefined
  try {
    return JSON.parse(decodeURIComponent(match[1])) as Record<string, string>
  } catch {
    return undefined
  }
}

/**
 * Client bootstrap for PostHog — initializes the SDK and bootstraps identity
 * from the experiment ID cookie. Experiment hooks read the singleton directly
 * from `@/lib/posthog` (not posthog-js/react context).
 *
 * SPA pageview tracking runs inside the PostHog SDK itself
 * (`capture_pageview: 'history_change'` in `lib/posthog.ts`), so this
 * component does NOT call `usePathname()`. That matters under Next 16
 * `cacheComponents: true`: calling `usePathname()` here would mark the
 * entire wrapped subtree (i.e. `{children}` = every page body) as dynamic
 * and force it to stream per-request after the static shell.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const initialized = useRef(false)

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const experimentId = getExperimentId()
    const bootstrapFlags = getBootstrapFlags()
    // Defer PostHog init until the browser is idle so its ~112 KB bundle
    // parse + the singleton's initial config sweep doesn't compete with
    // LCP-critical work on mobile. PostHog buffers events fired before
    // init() returns, so any `track.*` calls between mount and idle are
    // queued and flush on the first subsequent `capture()`.
    const idle =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? (cb: () => void) => window.requestIdleCallback(cb, { timeout: 3000 })
        : (cb: () => void) => window.setTimeout(cb, 2000)
    idle(() => {
      initPostHog(experimentId, bootstrapFlags)
    })
  }, [])

  return <>{children}</>
}
